
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { supabase, uploadCardAttachment } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { KanbanCardAttachment } from "@/types/database";
import { ImageIcon, Trash2, Upload } from "lucide-react";
import { toast } from "@/lib/toast";

interface CardCoverManagerProps {
  cardId: string;
  currentCoverUrl?: string;
  onCoverChange: (coverUrl: string | null) => void;
}

export function CardCoverManager({ cardId, currentCoverUrl, onCoverChange }: CardCoverManagerProps) {
  const [isUploading, setIsUploading] = useState(false);

  // Buscar anexos de imagem do cartão
  const { data: imageAttachments = [], refetch: refetchAttachments } = useQuery({
    queryKey: ["card_image_attachments", cardId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("kanban_card_attachments")
        .select("*")
        .eq("card_id", cardId)
        .like("file_type", "image/%")
        .order("created_at", { ascending: false });
        
      if (error) throw error;
      return data as KanbanCardAttachment[];
    }
  });

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    
    // Verificar se é uma imagem
    if (!file.type.startsWith('image/')) {
      toast.error("Por favor, selecione apenas arquivos de imagem");
      return;
    }
    
    setIsUploading(true);
    
    try {
      const result = await uploadCardAttachment(file, cardId);
      
      // Adicionar o anexo ao banco
      const { error } = await supabase
        .from("kanban_card_attachments")
        .insert({
          card_id: cardId,
          name: file.name,
          url: result.url,
          file_type: file.type,
          size: file.size
        });
        
      if (error) throw error;
      
      refetchAttachments();
      toast.success("Imagem enviada com sucesso!");
    } catch (error: any) {
      console.error("Erro ao fazer upload:", error);
      toast.error(`Erro ao enviar imagem: ${error.message}`);
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleSetCover = async (imageUrl: string) => {
    try {
      const { error } = await supabase
        .from("kanban_cards")
        .update({ cover_image_url: imageUrl })
        .eq("id", cardId);
        
      if (error) throw error;
      
      onCoverChange(imageUrl);
      toast.success("Capa definida com sucesso!");
    } catch (error) {
      console.error("Erro ao definir capa:", error);
      toast.error("Erro ao definir capa");
    }
  };

  const handleRemoveCover = async () => {
    try {
      const { error } = await supabase
        .from("kanban_cards")
        .update({ cover_image_url: null })
        .eq("id", cardId);
        
      if (error) throw error;
      
      onCoverChange(null);
      toast.success("Capa removida com sucesso!");
    } catch (error) {
      console.error("Erro ao remover capa:", error);
      toast.error("Erro ao remover capa");
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-medium mb-2">Capa do Cartão</h3>
        
        {currentCoverUrl && (
          <div className="mb-4">
            <div className="relative w-full h-32 bg-gray-100 rounded-md overflow-hidden">
              <img 
                src={currentCoverUrl} 
                alt="Capa atual"
                className="w-full h-full object-cover" 
              />
            </div>
            <Button
              onClick={handleRemoveCover}
              variant="outline"
              size="sm"
              className="mt-2 w-full"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Remover Capa
            </Button>
          </div>
        )}
        
        <div className="space-y-3">
          <div>
            <Label htmlFor="cover-upload" className="cursor-pointer">
              <div className="flex items-center gap-2 py-2 px-4 rounded-md bg-primary/10 hover:bg-primary/20 transition-colors">
                <Upload className="h-4 w-4" />
                <span>Enviar Nova Imagem</span>
              </div>
            </Label>
            <Input
              id="cover-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileUpload}
              disabled={isUploading}
            />
          </div>
          
          {imageAttachments.length > 0 && (
            <>
              <Separator />
              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  Escolher das imagens anexadas:
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {imageAttachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className={`relative group cursor-pointer rounded-md overflow-hidden border-2 transition-colors ${
                        currentCoverUrl === attachment.url 
                          ? 'border-primary' 
                          : 'border-transparent hover:border-gray-300'
                      }`}
                      onClick={() => handleSetCover(attachment.url)}
                    >
                      <div className="aspect-video bg-gray-100">
                        <img 
                          src={attachment.url} 
                          alt={attachment.name}
                          className="w-full h-full object-cover" 
                        />
                      </div>
                      {currentCoverUrl === attachment.url && (
                        <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                          <ImageIcon className="h-6 w-6 text-primary" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
